import React from 'react';
import {
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Chip
} from '@mui/material';

export const DataSourceSection = ({ 
  formik, 
  loading, 
  hasError, 
  getErrorMessage,
  dataSources,
  availableEndpoints,
  availableFilters,
  getOptionsForFilter
}) => {
  return (
    <Grid container spacing={2}>
      {/* Data Source Selection */}
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth>
          <InputLabel id="dataSource-label">Data Source</InputLabel>
          <Select
            labelId="dataSource-label"
            id="dataSource"
            name="dataSource"
            value={formik.values.dataSource}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            label="Data Source"
            error={hasError(formik, 'dataSource')}
            disabled={loading}
          >
            {dataSources.map((source) => (
              <MenuItem key={source.sourceId} value={source.sourceId}>
                {source.name}
              </MenuItem>
            ))}
          </Select>
          {hasError(formik, 'dataSource') && (
            <FormHelperText error>{getErrorMessage(formik, 'dataSource')}</FormHelperText>
          )}
        </FormControl>
      </Grid>
      
      {/* Source Endpoint Selection */}
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth>
          <InputLabel id="sourceEndpoint-label">Data Category</InputLabel>
          <Select
            labelId="sourceEndpoint-label"
            id="sourceEndpoint"
            name="sourceEndpoint"
            value={formik.values.sourceEndpoint}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            label="Data Category"
            error={hasError(formik, 'sourceEndpoint')}
            disabled={loading || availableEndpoints.length === 0}
          >
            {availableEndpoints.map((endpoint) => (
              <MenuItem key={endpoint.endpointId} value={endpoint.endpointId}>
                {endpoint.name}
              </MenuItem>
            ))}
          </Select>
          {hasError(formik, 'sourceEndpoint') && (
            <FormHelperText error>{getErrorMessage(formik, 'sourceEndpoint')}</FormHelperText>
          )}
        </FormControl>
      </Grid>
      
      {/* Source Filters */}
      {renderFilterControls(formik, loading, availableFilters, getOptionsForFilter)}
    </Grid>
  );
};

/**
 * Renders filter controls based on available filters
 */
const renderFilterControls = (formik, loading, availableFilters, getOptionsForFilter) => {
  return availableFilters.map((filter, filterIndex) => {
    const filterValueOptions = getOptionsForFilter(filter.filterId);
    const sourceFilter = formik.values.sourceFilters?.find(
      f => f.name === filter.name
    );
    
    return (
      <Grid item xs={12} key={filter.filterId}>
        <FormControl fullWidth>
          <InputLabel id={`filter-${filter.name}-label`}>
            {filter.displayName} {filter.isRequired && '*'}
          </InputLabel>
          <Select
            labelId={`filter-${filter.name}-label`}
            id={`filter-${filter.name}`}
            name={`sourceFilters[${filterIndex}].values`}
            value={sourceFilter?.values || []}
            onChange={(e) => {
              const newFilters = [...(formik.values.sourceFilters || [])];
              const filterIndex = newFilters.findIndex(
                f => f.name === filter.name
              );
              
              if (filterIndex >= 0) {
                newFilters[filterIndex].values = e.target.value;
              } else {
                newFilters.push({
                  name: filter.name,
                  values: e.target.value
                });
              }
              
              formik.setFieldValue('sourceFilters', newFilters);
            }}
            onBlur={formik.handleBlur}
            label={`${filter.displayName} ${filter.isRequired ? '*' : ''}`}
            multiple
            renderValue={(selected) => renderSelectedValues(selected, filterValueOptions)}
            disabled={loading}
          >
            <MenuItem value="*">
              <em>All {filter.displayName}</em>
            </MenuItem>
            
            {filterValueOptions.map((option) => (
              <MenuItem key={option.optionId} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>
            {filter.description}
          </FormHelperText>
        </FormControl>
      </Grid>
    );
  });
};

/**
 * Renders selected values as chips
 */
const renderSelectedValues = (selected, filterValueOptions) => {
  if (selected.includes('*')) {
    return <Chip label="All" />;
  }
  
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
      {selected.map((value) => {
        const option = filterValueOptions.find(
          opt => opt.value === value
        );
        return (
          <Chip 
            key={value} 
            label={option ? option.label : value} 
          />
        );
      })}
    </Box>
  );
};